/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type ImageLayer from 'ol/layer/Image';
import { useRef, useState, useEffect, useMemo } from 'react';
import { CustomTooltip, dateUtils } from '@opengeoweb/shared';
import { WMInvalidDateValues } from '@opengeoweb/webmap';
import type { WMJSDimension, WMLayer } from '@opengeoweb/webmap';
import type { TimeawareImageSource } from './TimeawareImageSource';

const getShortenedDimensionName = (dimName: string): string => {
  switch (dimName) {
    case 'time':
      return 'val';
    case 'reference_time':
      return 'ref';
    default: {
      const nameParts = dimName.split('_');
      if (nameParts[0] === 'pressure') {
        return 'p';
      }
      if (nameParts[0] === 'height') {
        return 'h';
      }
      return dimName;
    }
  }
};

const formatDimensionVal = (dimName: string, dimVal: string): string => {
  switch (dimName) {
    case 'time':
    case 'reference_time': {
      const date = dateUtils.stringToDate(
        dimVal,
        "yyyy-MM-dd'T'HH:mm:ss'Z'",
        true,
      );
      if (date) {
        return dateUtils.dateToString(date, "MM-dd HH:mm'Z'", true)!;
      }
      return dimVal;
    }
    default: {
      return dimVal;
    }
  }
};

const getDimensionInfo = (wmLayerDimension: WMJSDimension): string => {
  const dimensionName = getShortenedDimensionName(wmLayerDimension.name);
  const formattedValue = formatDimensionVal(
    wmLayerDimension.name,
    wmLayerDimension.currentValue,
  );
  return `${dimensionName} ${formattedValue}`;
};

export interface ShowTimeAwareLayerInfoProps {
  wmLayer: WMLayer;
  stepsToCheck: number[];
  layer: ImageLayer<TimeawareImageSource>;
  visible: boolean;
}

export const ShowTimeAwareLayerInfo: React.FC<ShowTimeAwareLayerInfoProps> = ({
  wmLayer,
  stepsToCheck: timeStepsToCheck,
  layer,
  visible,
}: ShowTimeAwareLayerInfoProps) => {
  const wmTimeDim = wmLayer?.dimensions?.find(
    (dimension) => dimension.name === 'time',
  );
  const intervalIdRef = useRef<NodeJS.Timeout>(undefined);
  const [render, setRender] = useState(0);

  // Sets a timer
  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      setRender(render + 1);
    }, 100);

    return (): void => {
      clearInterval(intervalIdRef.current);
    };
  }, [render]);

  const items = useMemo(() => {
    if (!timeStepsToCheck?.length) {
      return null;
    }
    const itemWidth = Math.round(1000 / timeStepsToCheck.length);
    return timeStepsToCheck.map((timeStep, index) => {
      const closestValue = wmTimeDim?.getClosestValueForTime(timeStep) || '';
      const requestedStepIsOutsideRange = WMInvalidDateValues.has(closestValue);
      const images =
        layer?.getSource()?.getAltGeoReferencedImages(closestValue) || [];

      const isSelectedStep =
        visible && wmTimeDim?.currentValue === closestValue;
      const colorImageIsLoading = layer
        ?.getSource()
        ?.hasImageLoadedForTimeValue(closestValue)
        ? '#40AF50'
        : '#666666';

      const colorImageIsAvailable =
        images?.length > 0 ? colorImageIsLoading : '#FF0000';
      const colorLayerIsOutsideRange = requestedStepIsOutsideRange
        ? '#5080FA'
        : colorImageIsAvailable;
      const height = requestedStepIsOutsideRange ? '15px' : '20px';
      const top = requestedStepIsOutsideRange ? '5px' : '2px';
      const left = `calc(25% + ${index * itemWidth}px)`;
      const opacity = isSelectedStep ? 1 : 0.8;
      return (
        <span
          key={timeStep}
          style={{
            background: isSelectedStep ? '#C0FF50' : colorLayerIsOutsideRange,
            display: 'block',
            position: 'absolute',
            width: `${itemWidth}px`,
            height,
            left,
            top,
            margin: 0,
            padding: 0,
            opacity,
          }}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, timeStepsToCheck, visible, wmTimeDim?.currentValue, render]);

  const tooltipText = (
    <span>
      {wmLayer?.title}: <br />
      {wmLayer?.dimensions.map((dim, index) => (
        <span key={`${dim.name}-${dim.currentValue}`}>
          {dim.name}: {formatDimensionVal(dim.name, dim.currentValue)}
          {index < wmLayer.dimensions.length - 1 && <br />}
        </span>
      ))}
    </span>
  );

  return (
    <CustomTooltip key={wmLayer.id} title={tooltipText}>
      <div
        data-testid="debug-info"
        style={{
          position: 'relative',
          width: 'calc(100% - 60px)',
          left: '50px',
          right: '10px',
          marginTop: '12px',
          marginBottom: '-10px',
        }}
      >
        <div style={{ width: '100%' }}>
          <div>{items}</div>
        </div>
        <div
          style={{
            left: '10px',
            paddingTop: '3px',
            fontSize: '12px',
            fontFamily: 'Roboto',
            opacity: 0.85,
            width: '100%',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              verticalAlign: 'top',
              width: '25%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '12px',
              paddingRight: '4px',
              paddingLeft: '4px',
              color: '#051039',
              backgroundColor: 'rgba(236, 237, 238, 0.7)',
              borderRadius: 2,
            }}
          >
            {wmLayer?.title}
          </span>
          <span
            style={{ display: 'inline-block', width: '75%', opacity: '0.7' }}
          >
            {wmLayer?.dimensions.map((wmLayerDimension) => {
              return (
                <span
                  key={wmLayerDimension.name}
                  style={{
                    margin: '0 0 0 0px',
                    padding: '1px 5px',
                    background: 'blue',
                    borderRadius: 2,
                    color: 'white',
                    fontSize: '11px',
                  }}
                  title={`${wmLayerDimension.name} ${formatDimensionVal(
                    wmLayerDimension.name,
                    wmLayerDimension.currentValue,
                  )}`}
                >
                  {getDimensionInfo(wmLayerDimension)}
                </span>
              );
            })}
          </span>
        </div>
      </div>
    </CustomTooltip>
  );
};
